import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { DashboardTab } from './components/features/DashboardTab';
import { Protocol16_8 } from './components/features/Protocols/Protocol16_8';
import { Protocol18_6 } from './components/features/Protocols/Protocol18_6';
import { ProtocolOMAD } from './components/features/Protocols/ProtocolOMAD';
import { ProtocolTMAW } from './components/features/Protocols/ProtocolTMAW';
import { ProtocolOMAW } from './components/features/Protocols/ProtocolOMAW';
import { ProtocolIRF } from './components/features/Protocols/ProtocolIRF';
import { CoachTab } from './components/features/CoachTab';
import { InsightsTab } from './components/features/InsightsTab';
import { BloodTab } from './components/features/BloodTab/BloodTab';
import { WeightTab } from './components/features/WeightTab';
import { ProgressTrackerTab } from './components/features/ProgressTracker/ProgressTrackerTab';
import { WeightProvider } from './context/WeightContext';
import { ShopTab } from './components/features/ShopTab';
import { ProfileTab } from './components/features/ProfileTab';
import { useAuth } from './context/AuthContext';
import { AgeGate } from './components/modals/AgeGate';
import { AuthModal } from './components/modals/AuthModal';
import { ModalProvider } from './context/ModalContext';
import { useParams } from 'react-router-dom';
import { ReadOnlyBanner } from './components/shared/ReadOnlyBanner';

function ProtocolRouter() {
  const { id } = useParams();

  const protocolComponents = {
    '16:8': <Protocol16_8 />,
    '18:6': <Protocol18_6 />,
    'OMAD': <ProtocolOMAD />,
    'TMAW': <ProtocolTMAW />,
    'OMAW': <ProtocolOMAW />,
    'IRF': <ProtocolIRF />,
  };

  return protocolComponents[id] || <div>Protocol not found</div>;
}

export function App() {
  const { currentUser, loading, isSubscribed } = useAuth();
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  useEffect(() => {
    const confirmed = localStorage.getItem('222f_age_confirmed');
    if (confirmed === 'true') {
      setAgeConfirmed(true);
    }
  }, []);

  const handleAgeConfirm = (isAdult: boolean) => {
    if (isAdult) {
      setAgeConfirmed(true);
      localStorage.setItem('222f_age_confirmed', 'true');
    } else {
      // Under 18 — redirect away
      window.location.href = 'https://222fasting.com';
    }
  };

  // 1. Age gate first
  if (!ageConfirmed) {
    return <AgeGate onConfirm={handleAgeConfirm} />;
  }

  // 2. Loading state while Supabase checks session
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-spin rounded-full border-b-2 border-primary h-8 w-8" />
      </div>
    );
  }

  // 3. No user — show auth screen (full screen, no close)
  if (!currentUser) {
    return (
      <ModalProvider>
        <WeightProvider>
          <AuthModal />
        </WeightProvider>
      </ModalProvider>
    );
  }

  // 4. Authenticated — show full app
  return (
    <ModalProvider>
      <WeightProvider>
        {(!isSubscribed && !loading && currentUser) && <ReadOnlyBanner />}
        <Layout>
          <Routes>
            <Route path="/" element={<DashboardTab />} />
            <Route path="/dashboard" element={<DashboardTab />} />
            <Route path="/protocol/:id" element={<ProtocolRouter />} />
            <Route path="/coach" element={<CoachTab />} />
            <Route path="/insights" element={<InsightsTab />} />
            <Route path="/blood" element={<BloodTab />} />
            <Route path="/weight" element={<WeightTab />} />
            <Route path="/progress" element={<ProgressTrackerTab />} />
            <Route path="/shop" element={<ShopTab />} />
            <Route path="/profile" element={<ProfileTab />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </WeightProvider>
    </ModalProvider>
  );
}
